<template>
  <aside class="flex h-full flex-col bg-base-100 border-r border-base-300/60">
    <div class="shrink-0 border-b border-base-300/60 px-4 py-3">
      <div class="flex items-center gap-2.5">
        <div class="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <span v-html="iconContacts" class="w-4 h-4"></span>
        </div>
        <div>
          <p class="text-sm font-semibold text-base-content">{{ t('contacts.menu.title') }}</p>
          <p class="text-xs text-base-content/50">{{ t('contacts.menu.subtitle') }}</p>
        </div>
      </div>
    </div>

    <nav class="flex-1 overflow-y-auto px-2 py-3 scrollbar-thin">
      <ul class="space-y-0.5">
        <li v-for="item in navItems" :key="item.to">
          <router-link
            :to="item.to"
            class="group flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm transition-all"
            :class="isActive(item.to)
              ? 'bg-primary/10 text-primary font-medium border-l-2 border-primary pl-2'
              : 'text-base-content/70 hover:bg-base-200 hover:text-base-content border-l-2 border-transparent pl-2'"
          >
            <span
              v-html="item.icon"
              class="shrink-0 opacity-70 group-hover:opacity-100"
              :class="{ 'opacity-100': isActive(item.to) }"
            ></span>
            <span class="truncate">{{ item.label }}</span>
          </router-link>
        </li>
      </ul>
    </nav>

    <div class="shrink-0 border-t border-base-300/60 p-3">
      <router-link
        to="/"
        class="btn btn-ghost btn-sm w-full justify-start gap-2"
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
          <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
        </svg>
        {{ t('contacts.menu.backToMain') }}
      </router-link>
    </div>
  </aside>
</template>

<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import useLocale from '../../compositions/useLocale';

const { t } = useLocale();
const route = useRoute();

const iconHome = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" /></svg>`;
const iconCreate = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>`;
const iconList = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M8.25 6.75h12M8.25 12h12m-12 5.25h12M3.75 6.75h.007v.008H3.75V6.75Zm0 5.25h.007v.008H3.75V12Zm0 5.25h.007v.008H3.75v-.008Z" /></svg>`;
const iconSearch = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" /></svg>`;
const iconContacts = `<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4"><path stroke-linecap="round" stroke-linejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 0 0 2.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 0 1-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 0 0-1.091-.852H4.5A2.25 2.25 0 0 0 2.25 4.5v2.25Z" /></svg>`;

const navItems = computed(() => [
  { to: '/contacts', label: t('contacts.nav.home'), icon: iconHome },
  { to: '/contacts/create', label: t('contacts.nav.create'), icon: iconCreate },
  { to: '/contacts/list', label: t('contacts.nav.list'), icon: iconList },
  { to: '/contacts/search', label: t('contacts.nav.search'), icon: iconSearch },
]);

const isActive = (path) => {
  if (path === '/contacts') {
    return route.path === '/contacts';
  }
  if (path === '/contacts/list') {
    return (
      route.path.startsWith('/contacts/list')
      || route.path.startsWith('/contacts/detail')
      || route.path.startsWith('/contacts/update')
    );
  }
  return route.path === path || route.path.startsWith(`${path}/`);
};
</script>

<style scoped>
.scrollbar-thin {
  scrollbar-width: thin;
}
</style>
